import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Copy, CheckCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { copyFormattedContent } from "@/lib/utils";

interface ProductHuntEditorProps {
  content: {
    taglines: string[];
    short_descriptions: string[];
    full_descriptions: string[];
  } | null;
  onContentChange?: (type: string, index: number, value: string) => void;
  onSelectionChange?: (type: string, index: number) => void;
  userName: string;
  onUserNameChange?: (name: string) => void;
}

export const ProductHuntEditor: React.FC<ProductHuntEditorProps> = ({
  content,
  onContentChange,
  onSelectionChange,
  userName,
  onUserNameChange
}) => {
  const { toast } = useToast();
  const [copiedItem, setCopiedItem] = useState<{ type: string; index: number } | null>(null);
  const [selectedTagline, setSelectedTagline] = useState<number>(0);
  const [selectedShortDesc, setSelectedShortDesc] = useState<number>(0);
  const [selectedFullDesc, setSelectedFullDesc] = useState<number>(0);

  // Set the full description index to 0 on component mount or when content changes
  useEffect(() => {
    if (content && onSelectionChange) {
      // Ensure full description index is always 0
      onSelectionChange('fullDesc', 0);
    }
  }, [content, onSelectionChange]);

  const handleContentChange = (type: string, index: number, value: string) => {
    if (onContentChange) {
      onContentChange(type, index, value);
    }
  };

  const handleSelectionChange = (type: string, index: number) => {
    // Update local state
    if (type === 'tagline') {
      setSelectedTagline(index);
    } else if (type === 'shortDesc') {
      setSelectedShortDesc(index);
    } else if (type === 'fullDesc') {
      setSelectedFullDesc(0); // Always set to 0 since we only have one full description
      index = 0; // Ensure index is 0
    }
    
    // Notify parent component
    if (onSelectionChange) {
      onSelectionChange(type, index);
    }
  };

  const handleUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onUserNameChange) {
      onUserNameChange(e.target.value);
    }
  };

  const copyToClipboard = (text: string, type: string, index: number) => {
    copyFormattedContent(text, {
      onSuccess: () => {
        setCopiedItem({ type, index });
        toast({
          title: "Copied to clipboard",
          description: "Content has been copied to your clipboard",
        });
        setTimeout(() => setCopiedItem(null), 2000);
      },
      onError: (err) => {
        console.error('Copy failed:', err);
        toast({
          title: "Copy failed",
          description: "Could not copy to clipboard",
          variant: "destructive",
        });
      }
    });
  };

  if (!content) {
    return null;
  }

  return (
    <div className="space-y-8">
      {/* Product Name Section */}
      <div className="space-y-2">
        <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Your Name (for preview)</div>
        <Input 
          value={userName}
          onChange={handleUserNameChange}
          className="max-w-md"
          placeholder="Your name"
        />
      </div>
      
      {/* Taglines Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-zinc-800 dark:text-zinc-200">Taglines</h3>
          <div className="text-sm text-zinc-500">Max 60 characters</div>
        </div>
        <div className="grid gap-3">
          {content.taglines.map((tagline, index) => (
            <div 
              key={`tagline-${index}`}
              className={`relative group p-4 border rounded-lg hover:shadow-sm transition-all cursor-pointer ${
                selectedTagline === index ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50/30 dark:bg-blue-900/10' : 'bg-background'
              }`}
              onClick={() => {
                setSelectedTagline(index);
                handleSelectionChange('tagline', index);
              }}
            >
              <div className="pr-10 font-medium">
                {tagline}
              </div>
              <div className="absolute top-3 right-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(tagline, 'tagline', index);
                  }}
                >
                  {copiedItem?.type === 'tagline' && copiedItem?.index === index ? (
                    <CheckCheck className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="text-xs text-zinc-500 mt-2">
                {tagline.length}/60 characters
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Short Descriptions Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-zinc-800 dark:text-zinc-200">Short Descriptions</h3>
          <div className="text-sm text-zinc-500">Max 260 characters</div>
        </div>
        <div className="grid gap-3">
          {content.short_descriptions.map((description, index) => (
            <div 
              key={`desc-${index}`}
              className={`relative group p-4 border rounded-lg hover:shadow-sm transition-all cursor-pointer ${
                selectedShortDesc === index ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50/30 dark:bg-blue-900/10' : 'bg-background'
              }`}
              onClick={() => {
                setSelectedShortDesc(index);
                handleSelectionChange('shortDesc', index);
              }}
            >
              <div className="pr-10 text-base">
                {description}
              </div>
              <div className="absolute top-3 right-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(description, 'desc', index);
                  }}
                >
                  {copiedItem?.type === 'desc' && copiedItem?.index === index ? (
                    <CheckCheck className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="text-xs text-zinc-500 mt-2">
                {description.length}/260 characters
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Full Description Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-zinc-800 dark:text-zinc-200">Full Description</h3>
          <div className="text-sm text-zinc-500">Description for your Product Hunt launch</div>
        </div>
        
        {content.full_descriptions && content.full_descriptions.length > 0 ? (
          <div className="relative p-4 border rounded-lg bg-background hover:shadow-sm transition-all">
            <div className="absolute top-4 right-4 flex space-x-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => copyToClipboard(content.full_descriptions?.[0] || "", 'full', 0)}
                disabled={!content.full_descriptions?.[0]}
              >
                {copiedItem?.type === 'full' && copiedItem?.index === 0 ? (
                  <>
                    <CheckCheck className="h-4 w-4 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="pt-10 whitespace-pre-wrap">
              {content.full_descriptions?.[0] || "No content available. Generate content to see results."}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center border rounded-lg bg-background">
            <p className="text-zinc-500">No full description available</p>
          </div>
        )}
      </div>
    </div>
  );
};
